import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { BreakRule, BreakRuleImpact } from "@/shared/models/BreakRule";
import {
	deleteBreakRule,
	getBreakRuleImpact,
	upsertBreakRule,
} from "@/shared/repositories/BreakRuleRepository";
import { invalidatePlanQueries } from "@/shared/utils/planCache";
import type { BreakRuleModal } from "./useTableData";

type Change = {
	rule: BreakRule;
	action: Exclude<BreakRuleModal, "">;
	includeAdjusted?: boolean;
};

export const useBreakRuleActions = (
	modal: BreakRuleModal,
	onHide: () => void,
) => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");
	const [pending, setPending] = useState<
		(Change & { impact: BreakRuleImpact }) | null
	>(null);
	const [includeAdjusted, setIncludeAdjusted] = useState(false);

	const save = useMutation({
		mutationFn: async ({ rule, action, includeAdjusted = false }: Change) => {
			const response =
				action === "delete"
					? await deleteBreakRule(rule.BreakRuleId as number, includeAdjusted)
					: await upsertBreakRule(rule);
			if (!response.ok) throw new Error(response.message);
		},
		onSuccess: (_, { action }) => {
			setNotificationMessage(
				`The break was ${action === "create" ? "created" : action === "delete" ? "deleted" : "updated"} successfully`,
			);
			onHide();
		},
		onError: (e: Error) => setError(e.message),
		onSettled: (_, __, { rule }) =>
			invalidatePlanQueries(queryClient, {
				assetIds: [rule.AssetId],
				breaks: true,
			}),
	});
	const impact = useMutation({
		mutationFn: ({ rule }: Change) =>
			getBreakRuleImpact(rule.BreakRuleId as number),
		onSuccess: (impact, change) => setPending({ ...change, impact }),
		onError: (e: Error) => setError(e.message),
	});

	useEffect(() => {
		setPending(null);
		setIncludeAdjusted(false);
	}, [modal]);

	const submit = (rule: BreakRule) => {
		if (!modal) return;
		const change = { rule, action: modal };
		if (modal === "create") save.mutate(change);
		else impact.mutate(change);
	};
	const confirm = (proceed: boolean) => {
		if (proceed && pending) save.mutate({ ...pending, includeAdjusted });
		setPending(null);
	};

	return {
		submit,
		confirm,
		pending,
		includeAdjusted,
		setIncludeAdjusted,
		isBusy: save.isLoading || impact.isLoading,
		error,
		clearError: () => setError(""),
		notificationMessage,
		clearNotification: () => setNotificationMessage(""),
	};
};
